/**
 * BlogCreationModeDrawer Component
 *
 * Presents a choice between manual blog post creation and AI-powered generation.
 * Shown when user clicks "New Post" button.
 *
 * Story: UX-001, Migration to drawer pattern
 */
import { FileEdit, Sparkles, Rocket } from 'lucide-react'
import { cn } from '@/lib/utils'

interface BlogCreationModeDrawerProps {
  onSelectManual: () => void
  onSelectAI: () => void
  onSelectReleasePost: () => void
}

export function BlogCreationModeDrawer({
  onSelectManual,
  onSelectAI,
  onSelectReleasePost,
}: BlogCreationModeDrawerProps) {
  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-foreground mb-2">Create a New Article</h2>
        <p className="text-muted-foreground">
          Choose how you'd like to create your article
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {/* Manual Creation Option */}
        <button
          onClick={onSelectManual}
          className={cn(
            'group relative p-6 rounded-lg border-2 transition-all',
            'bg-background hover:bg-primary/5 border-border hover:border-primary',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
            'text-left flex flex-col items-start gap-4'
          )}
        >
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
              <FileEdit className="h-6 w-6" />
            </div>
            <h3 className="text-lg font-semibold text-foreground">
              Write from Scratch
            </h3>
          </div>
          <p className="text-sm text-muted-foreground">
            Use our full-featured TipTap editor to write and format your article manually.
            Perfect for when you have specific content in mind.
          </p>
          <div className="mt-2 text-xs text-muted-foreground">
            • Full control over content
            <br />
            • Rich text editor
            <br />
            • Immediate publishing
          </div>
        </button>

        {/* AI Writer Option */}
        <button
          onClick={onSelectAI}
          className={cn(
            'group relative p-6 rounded-lg border-2 transition-all',
            'bg-background hover:bg-primary/5 border-border hover:border-primary',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
            'text-left flex flex-col items-start gap-4'
          )}
        >
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
              <Sparkles className="h-6 w-6" />
            </div>
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              AI Writer
              <span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary font-normal">
                New
              </span>
            </h3>
          </div>
          <p className="text-sm text-muted-foreground">
            Answer 10 strategic questions and let AI write a complete, SEO-optimized article
            for you. Review and approve at each stage.
          </p>
          <div className="mt-2 text-xs text-muted-foreground">
            • Answer 10 simple questions
            <br />
            • AI researches and writes
            <br />
            • Review headlines & outline
            <br />
            • Edit after generation
          </div>
        </button>

        {/* Release Post Generator Option */}
        <button
          onClick={onSelectReleasePost}
          className={cn(
            'group relative p-6 rounded-lg border-2 transition-all',
            'bg-background hover:bg-primary/5 border-border hover:border-primary',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
            'text-left flex flex-col items-start gap-4'
          )}
        >
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
              <Rocket className="h-6 w-6" />
            </div>
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              Release Post Generator
              <span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary font-normal">
                Beta
              </span>
            </h3>
          </div>
          <p className="text-sm text-muted-foreground">
            Paste a Loom demo video URL and automatically generate a complete release announcement
            for blog and email campaigns.
          </p>
          <div className="mt-2 text-xs text-muted-foreground">
            • Paste Loom video URL
            <br />
            • AI extracts features
            <br />
            • Generates article + email
            <br />
            • Creates Klaviyo campaign
          </div>
        </button>
      </div>
    </div>
  )
}
